import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import styles from './securityTable.module.scss';

export default function SecurityTable({
  rows = [], loading = false, searched = false, onJsonView
}) {
  const { t } = useTranslation();
  const [order, setOrder] = useState('desc');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleToggleSort = () => {
    setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = Number(event.target.value) || 10;
    setRowsPerPage(next);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const parseDate = useCallback((isoString) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  const formatDateTime = useCallback(
    (isoString) => {
      const date = parseDate(isoString);
      if (!date) return '';
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    [parseDate]
  );

  const sortedRows = useMemo(() => {
    const rowsToSort = Array.isArray(rows) ? [...rows] : [];
    rowsToSort.sort((a, b) => {
      const ascending = order === 'asc';
      const aMs = parseDate(a?.published)?.getTime() ?? Number.NEGATIVE_INFINITY;
      const bMs = parseDate(b?.published)?.getTime() ?? Number.NEGATIVE_INFINITY;

      // As a tie-breaker, sort by default position
      if (aMs === bMs) {
        return ascending ? a.position - b.position : b.position - a.position;
      }

      return ascending ? aMs - bMs : bMs - aMs;
    });
    return rowsToSort;
  }, [rows, order, parseDate]);

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const resultsCount = sortedRows.length;
  const resultsWord = resultsCount === 1 ? t('result') : t('results');
  const statusLabel = loading ? t('loading') : `${resultsCount} ${resultsWord}`;

  const renderTableBody = () => {
    if (!searched) {
      return ''
    }

    if (sortedRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            {t('user-activities-security-table-no-records')}
          </TableCell>
        </TableRow>
      );
    }

    return pagedRows.map((entry) => (
      <TableRow
        className={styles.tableBodyRow}
        key={entry?.rowKey ?? `${entry?.published}-${entry?.eventType}`}
        hover
      >
        <TableCell className={styles.tableCell}>{entry?.position}</TableCell>
        <TableCell className={styles.tableCell}>{entry?.displayMessage ?? ''}</TableCell>
        <TableCell className={styles.tableCell}>{entry?.severity ?? ''}</TableCell>
        <TableCell className={styles.tableCell}>{formatDateTime(entry?.published ?? '')}</TableCell>
        <TableCell className={styles.tableCell}>{entry?.outcome?.result ?? ''}</TableCell>
        <TableCell className={styles.tableCell}>
          <Button
            onClick={() => { onJsonView(entry) }}
            variant="contained"
            className="primary-btn"
            disableElevation
            sx={{
                color: '#000000',
                width: '147px',
                height: '40px',
                fontWeight: 700,
                backgroundColor: 'rgba(98, 81, 55, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(102,90,71, 0.20)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(138, 115, 92, 0.05)',
                },
                '&:focus': {
                  backgroundColor: 'rgba(112, 99, 79, 0.1)',
                },
              }}
          >
            <Box
              sx={{
                position: 'relative',
                border: '2px solid black !important',
                width: '13px !important',
                height: '13px !important',
                p: '0 !important',
                marginRight: '10px'

                }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  borderBottom: '2px solid black !important',
                  p: '0 !important',
                }}
              />
              <ArrowDropDownIcon
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -40%)',
                  fontSize: 17
                }}
              />
            </Box>
            {t('label-open-json-file')}
          </Button>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className={styles.container}>
      <Box sx={{ mb: 1 }}>
        <Typography
          className={styles.results}
          sx={{
            verticalAlign: 'middle',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {(searched || loading) ? statusLabel : ''}
        </Typography>
      </Box>

      <div className={styles.formContainer}>
        <TableContainer className={styles.tableContainer} component={Paper} elevation={0}>
          <Table stickyHeader size="medium" aria-label="Security Events Table">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    backgroundColor: '#F9F8F7',
                    padding: '0 10px',
                  },
                }}
              >
                <TableCell className={styles.userTableHeader} sx={{ width: '50px' }}>
                  <div className={styles.userTableHeaderLabel}>#</div>
                </TableCell>

                <TableCell className={styles.userTableHeader} sx={{ width: '240px' }}>
                  <div className={styles.userTableHeaderLabel}>
                    {t('user-activities-security-table-display-message')}
                  </div>
                </TableCell>

                <TableCell className={styles.userTableHeaderDetails} sx={{ width: '137px' }}>
                  {t('user-activities-security-table-severity')}
                </TableCell>

                <TableCell className={styles.userTableHeader} sx={{ width: '240px' }} sortDirection={order}>
                  <TableSortLabel
                    className={styles.userTableHeaderLabel}
                    active
                    direction={order}
                    onClick={handleToggleSort}
                  >
                    {t('user-activities-security-table-published-timestamp')}
                  </TableSortLabel>
                </TableCell>

                <TableCell className={styles.userTableHeader} sx={{ width: '215px' }}>
                  <div className={styles.userTableHeaderLabel}>
                    {t('user-activities-security-table-result')}
                  </div>
                </TableCell>

                <TableCell className={styles.userTableHeader} sx={{ width: '240px' }}>
                  <div className={styles.userTableHeaderLabel}>
                    {t('user-activities-security-table-additional-detail')}
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell sx={{ border: 'none' }} colSpan={6}>
                    <div className={styles.loaderContainer}>
                      <div className={styles.loading}>{t('loading')}</div>
                      <CircularProgress sx={{ color: 'black' }} />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                renderTableBody()
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {searched && sortedRows.length > rowsPerPage && !loading && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('rows-per-page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('rows-per-page-of')} ${count}`}
          />
        )}
      </div>
    </div>
  );
}

SecurityTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      rowKey: PropTypes.string,
      position: PropTypes.number,
      displayMessage: PropTypes.string,
      severity: PropTypes.string,
      published: PropTypes.string,
      outcome: PropTypes.shape({
        result: PropTypes.string,
      }),
      eventType: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  searched: PropTypes.bool,
  onJsonView: PropTypes.func.isRequired,
};

SecurityTable.defaultProps = {
  rows: [],
  loading: false,
  searched: false,
};
